// ABOUTME: Theme token catalog, brand defaults, palette presets and CSS generation for the admin design panel.
// ABOUTME: Turns stored token values into a stylesheet that overrides the :root and .dark variables from index.css.

export type ThemeTokens = Record<string, string>;

export interface ThemeTypography {
  fontHeading: string;
  fontBody: string;
  baseFontSize: number;
  radius: number;
  shadowIntensity: 'soft' | 'medium' | 'strong';
  gradientHero: [string, string];
  gradientAccent: [string, string];
  gradientCard: [string, string];
}

export interface ThemeSettings {
  tokens_light: ThemeTokens;
  tokens_dark: ThemeTokens;
  typography: Partial<ThemeTypography>;
}

/** Color tokens exposed in the admin panel, grouped for the UI. */
export const COLOR_TOKEN_GROUPS: { group: string; tokens: string[] }[] = [
  { group: 'surface', tokens: ['background', 'foreground', 'card', 'card-foreground', 'popover', 'popover-foreground'] },
  { group: 'brand', tokens: ['primary', 'primary-foreground', 'primary-light', 'secondary', 'secondary-foreground', 'accent', 'accent-foreground'] },
  { group: 'neutral', tokens: ['muted', 'muted-foreground', 'border', 'input', 'ring'] },
  { group: 'status', tokens: ['success', 'success-foreground', 'warning', 'warning-foreground', 'info', 'info-foreground', 'destructive', 'destructive-foreground'] },
  { group: 'sidebar', tokens: ['sidebar-background', 'sidebar-foreground', 'sidebar-primary', 'sidebar-primary-foreground', 'sidebar-accent', 'sidebar-accent-foreground', 'sidebar-border', 'sidebar-ring'] },
];

export const COLOR_TOKENS = COLOR_TOKEN_GROUPS.flatMap((g) => g.tokens);

/** Light values from index.css — the MnemoSpeak brand theme. */
export const BRAND_LIGHT: ThemeTokens = {
  background: '42 100% 93%',
  foreground: '219 73% 32%',
  card: '0 0% 100%',
  'card-foreground': '219 73% 32%',
  popover: '0 0% 100%',
  'popover-foreground': '219 73% 32%',
  primary: '219 73% 32%',
  'primary-foreground': '0 0% 100%',
  'primary-light': '219 37% 47%',
  secondary: '34 90% 79%',
  'secondary-foreground': '219 73% 32%',
  accent: '34 94% 69%',
  'accent-foreground': '219 73% 32%',
  muted: '42 40% 90%',
  'muted-foreground': '219 30% 45%',
  border: '210 92% 92%',
  input: '210 92% 92%',
  ring: '219 73% 32%',
  success: '152 69% 31%',
  'success-foreground': '0 0% 100%',
  warning: '34 94% 69%',
  'warning-foreground': '219 73% 32%',
  info: '210 80% 52%',
  'info-foreground': '0 0% 100%',
  destructive: '0 72% 46%',
  'destructive-foreground': '0 0% 100%',
  'sidebar-background': '0 0% 100%',
  'sidebar-foreground': '219 73% 32%',
  'sidebar-primary': '219 73% 32%',
  'sidebar-primary-foreground': '0 0% 100%',
  'sidebar-accent': '34 90% 79%',
  'sidebar-accent-foreground': '219 73% 32%',
  'sidebar-border': '210 92% 92%',
  'sidebar-ring': '219 73% 32%',
};

/** Dark values from index.css. */
export const BRAND_DARK: ThemeTokens = {
  background: '220 30% 8%',
  foreground: '210 20% 95%',
  card: '220 25% 12%',
  'card-foreground': '210 20% 95%',
  popover: '220 25% 12%',
  'popover-foreground': '210 20% 95%',
  primary: '219 60% 50%',
  'primary-foreground': '0 0% 100%',
  'primary-light': '219 45% 58%',
  secondary: '34 50% 30%',
  'secondary-foreground': '210 20% 95%',
  accent: '34 80% 55%',
  'accent-foreground': '0 0% 100%',
  muted: '220 15% 16%',
  'muted-foreground': '220 10% 60%',
  border: '220 15% 20%',
  input: '220 15% 20%',
  ring: '219 60% 50%',
  success: '152 55% 42%',
  'success-foreground': '0 0% 100%',
  warning: '34 80% 55%',
  'warning-foreground': '220 30% 8%',
  info: '210 70% 58%',
  'info-foreground': '0 0% 100%',
  destructive: '0 62% 45%',
  'destructive-foreground': '0 0% 100%',
  'sidebar-background': '220 25% 10%',
  'sidebar-foreground': '210 20% 90%',
  'sidebar-primary': '219 60% 50%',
  'sidebar-primary-foreground': '0 0% 100%',
  'sidebar-accent': '34 50% 30%',
  'sidebar-accent-foreground': '210 20% 90%',
  'sidebar-border': '220 15% 20%',
  'sidebar-ring': '219 60% 50%',
};

/** Font pairs allowed in the panel; each entry lists the Google Fonts families to load. */
export const FONT_PAIRS: { heading: string; body: string; families: string[] }[] = [
  { heading: 'Manrope', body: 'Inter', families: ['Manrope:wght@400;500;600;700;800', 'Inter:wght@300;400;500;600;700;800'] },
  { heading: 'Space Grotesk', body: 'DM Sans', families: ['Space+Grotesk:wght@400;500;600;700', 'DM+Sans:wght@400;500;700'] },
  { heading: 'Outfit', body: 'Figtree', families: ['Outfit:wght@400;500;600;700', 'Figtree:wght@400;500;600;700'] },
  { heading: 'Sora', body: 'Manrope', families: ['Sora:wght@400;500;600;700', 'Manrope:wght@400;500;600;700'] },
  { heading: 'Lora', body: 'Nunito Sans', families: ['Lora:wght@400;500;600;700', 'Nunito+Sans:wght@400;500;600;700'] },
  { heading: 'Libre Baskerville', body: 'IBM Plex Sans', families: ['Libre+Baskerville:wght@400;700', 'IBM+Plex+Sans:wght@400;500;600;700'] },
];

export const DEFAULT_TYPOGRAPHY: ThemeTypography = {
  fontHeading: 'Manrope',
  fontBody: 'Inter',
  baseFontSize: 16,
  radius: 0.75,
  shadowIntensity: 'medium',
  gradientHero: ['219 73% 32%', '219 37% 47%'],
  gradientAccent: ['34 94% 69%', '34 90% 79%'],
  gradientCard: ['0 0% 100%', '42 100% 93%'],
};

/** Ready-made palettes; each only sets the colors that define the mood. */
export const THEME_PRESETS: { key: string; label: string; light: ThemeTokens; dark: ThemeTokens }[] = [
  { key: 'brand', label: 'MnemoSpeak', light: BRAND_LIGHT, dark: BRAND_DARK },
  {
    key: 'ocean',
    label: 'Ocean Deep',
    light: { ...BRAND_LIGHT, background: '200 40% 97%', foreground: '208 69% 15%', primary: '208 69% 15%', 'primary-light': '196 55% 40%', secondary: '186 40% 82%', 'secondary-foreground': '208 69% 15%', accent: '186 55% 52%', 'accent-foreground': '0 0% 100%', muted: '200 25% 92%', 'muted-foreground': '208 25% 40%', border: '200 30% 88%', input: '200 30% 88%', ring: '208 69% 15%', 'sidebar-accent': '186 40% 82%', 'sidebar-primary': '208 69% 15%', 'sidebar-border': '200 30% 88%' },
    dark: { ...BRAND_DARK, background: '208 45% 9%', primary: '196 60% 48%', 'primary-light': '196 50% 58%', accent: '186 55% 45%', secondary: '196 30% 25%' },
  },
  {
    key: 'forest',
    label: 'Forest & Moss',
    light: { ...BRAND_LIGHT, background: '80 25% 96%', foreground: '150 40% 17%', primary: '150 40% 17%', 'primary-light': '145 25% 38%', secondary: '110 25% 80%', 'secondary-foreground': '150 40% 17%', accent: '115 27% 60%', 'accent-foreground': '150 40% 12%', muted: '95 20% 90%', 'muted-foreground': '150 15% 38%', border: '95 22% 86%', input: '95 22% 86%', ring: '150 40% 17%', 'sidebar-accent': '110 25% 80%', 'sidebar-primary': '150 40% 17%', 'sidebar-border': '95 22% 86%' },
    dark: { ...BRAND_DARK, background: '150 25% 8%', primary: '145 40% 45%', 'primary-light': '145 32% 55%', accent: '115 30% 48%', secondary: '145 20% 22%' },
  },
  {
    key: 'noir',
    label: 'Noir & Gold',
    light: { ...BRAND_LIGHT, background: '40 20% 96%', foreground: '0 0% 8%', primary: '0 0% 10%', 'primary-light': '0 0% 30%', secondary: '43 55% 75%', 'secondary-foreground': '0 0% 10%', accent: '43 52% 55%', 'accent-foreground': '0 0% 10%', muted: '40 12% 90%', 'muted-foreground': '0 0% 40%', border: '40 12% 86%', input: '40 12% 86%', ring: '0 0% 10%', 'sidebar-accent': '43 55% 75%', 'sidebar-primary': '0 0% 10%', 'sidebar-border': '40 12% 86%' },
    dark: { ...BRAND_DARK, background: '0 0% 6%', card: '0 0% 10%', popover: '0 0% 10%', primary: '43 52% 55%', 'primary-foreground': '0 0% 8%', 'primary-light': '43 45% 65%', accent: '43 60% 62%', 'accent-foreground': '0 0% 8%', secondary: '0 0% 18%', border: '0 0% 20%', input: '0 0% 20%' },
  },
  {
    key: 'sand',
    label: 'Warm Sand',
    light: { ...BRAND_LIGHT, background: '35 40% 97%', foreground: '28 25% 25%', primary: '28 25% 32%', 'primary-light': '30 22% 48%', secondary: '38 35% 85%', 'secondary-foreground': '28 25% 25%', accent: '35 35% 65%', 'accent-foreground': '28 25% 20%', muted: '36 25% 92%', 'muted-foreground': '28 15% 45%', border: '36 25% 88%', input: '36 25% 88%', ring: '28 25% 32%', 'sidebar-accent': '38 35% 85%', 'sidebar-primary': '28 25% 32%', 'sidebar-border': '36 25% 88%' },
    dark: { ...BRAND_DARK, background: '28 18% 9%', primary: '35 40% 55%', 'primary-light': '35 32% 64%', accent: '35 45% 60%', secondary: '30 20% 22%' },
  },
];

const SHADOW_SETS: Record<ThemeTypography['shadowIntensity'], Record<string, string>> = {
  soft: {
    '--shadow-sm': '0 1px 2px hsl(var(--primary) / 0.04)',
    '--shadow-md': '0 2px 8px hsl(var(--primary) / 0.05)',
    '--shadow-lg': '0 8px 20px hsl(var(--primary) / 0.07)',
    '--shadow-primary': '0 2px 10px hsl(var(--primary) / 0.15)',
  },
  medium: {
    '--shadow-sm': '0 1px 3px hsl(var(--primary) / 0.06)',
    '--shadow-md': '0 4px 12px hsl(var(--primary) / 0.08)',
    '--shadow-lg': '0 12px 32px hsl(var(--primary) / 0.12)',
    '--shadow-primary': '0 4px 16px hsl(var(--primary) / 0.25)',
  },
  strong: {
    '--shadow-sm': '0 2px 5px hsl(var(--primary) / 0.1)',
    '--shadow-md': '0 6px 18px hsl(var(--primary) / 0.16)',
    '--shadow-lg': '0 18px 44px hsl(var(--primary) / 0.22)',
    '--shadow-primary': '0 6px 22px hsl(var(--primary) / 0.38)',
  },
};

const isValidTriplet = (value: unknown): value is string =>
  typeof value === 'string' && /^-?[\d.]+\s+[\d.]+%\s+[\d.]+%$/.test(value.trim());

/** Builds the CSS custom property declarations for one theme mode. */
export const tokensToDeclarations = (tokens: ThemeTokens): Record<string, string> => {
  const out: Record<string, string> = {};
  for (const token of COLOR_TOKENS) {
    const value = tokens[token];
    if (isValidTriplet(value)) out[`--${token}`] = value.trim();
  }
  return out;
};

/** Non-color declarations (fonts, radius, shadows, gradients) shared by both modes. */
export const typographyToDeclarations = (typography: Partial<ThemeTypography>): Record<string, string> => {
  const t = { ...DEFAULT_TYPOGRAPHY, ...typography };
  const gradient = (pair: [string, string]) =>
    `linear-gradient(135deg, hsl(${pair[0]}), hsl(${pair[1]}))`;
  return {
    '--font-heading': `'${t.fontHeading}', system-ui, sans-serif`,
    '--font-body': `'${t.fontBody}', system-ui, sans-serif`,
    '--radius': `${t.radius}rem`,
    ...SHADOW_SETS[t.shadowIntensity] ?? SHADOW_SETS.medium,
    '--gradient-hero': gradient(t.gradientHero),
    '--gradient-accent': gradient(t.gradientAccent),
    '--gradient-card': `linear-gradient(180deg, hsl(${t.gradientCard[0]}), hsl(${t.gradientCard[1]}))`,
  };
};

const block = (selector: string, decls: Record<string, string>): string => {
  const body = Object.entries(decls).map(([k, v]) => `  ${k}: ${v};`).join('\n');
  return body ? `${selector} {\n${body}\n}` : '';
};

/** Generates the stylesheet injected site-wide for the saved theme. */
export const generateThemeCss = (settings: ThemeSettings): string => {
  const typo = typographyToDeclarations(settings.typography || {});
  const light = tokensToDeclarations(settings.tokens_light || {});
  const dark = tokensToDeclarations(settings.tokens_dark || {});
  const baseSize = { ...DEFAULT_TYPOGRAPHY, ...settings.typography }.baseFontSize;
  return [
    block(':root', { ...typo, ...light }),
    block('.dark', dark),
    baseSize && baseSize !== 16 ? `html {\n  font-size: ${baseSize}px;\n}` : '',
  ].filter(Boolean).join('\n\n');
};

/** Google Fonts stylesheet URL for the fonts used by a theme. */
export const fontsHref = (typography: Partial<ThemeTypography>): string | null => {
  const t = { ...DEFAULT_TYPOGRAPHY, ...typography };
  const families = new Set<string>();
  for (const name of [t.fontHeading, t.fontBody]) {
    const pair = FONT_PAIRS.find((p) => p.heading === name || p.body === name);
    const spec = pair?.families.find((f) => f.startsWith(name.replace(/ /g, '+') + ':'));
    if (spec) families.add(spec);
  }
  if (families.size === 0) return null;
  return `https://fonts.googleapis.com/css2?${[...families].map((f) => `family=${f}`).join('&')}&display=swap`;
};
